import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { UserService } from '../services/user.service';

/// GET /api/Promotion/GetWelcomeDeal. The server decides the discount when the payment is
/// opened (Databases/Services/Promotions/WelcomeDeals.cs); this only lets the pages show the
/// same figure first, so the total a buyer sees is the total they are charged.
export interface WelcomeDeal {
    Active: boolean;
    Eligible: boolean;
    Code?: string;
    Kind?: 'percent' | 'fixed';
    Value?: number;
    MaxDiscount?: number | null;
    MinimumPurchase?: number;
}

/// GET /api/Promotion/GetFreeShipping: free delivery on orders whose items come to Over or more,
/// up to Covers of it (Databases/Services/Promotions/FreeShippingOffer.cs). The same for everyone,
/// so it is read on the server render too - the home page's badge is in the page crawlers get.
export interface FreeShippingOffer {
    Active: boolean;
    Over?: number;
    Covers?: number;
}

/// The server render's offer, carried in the page so the browser neither asks again nor drops the badge.
const FREE_SHIPPING = makeStateKey<FreeShippingOffer>('free-shipping');

@Injectable({
    providedIn: 'root',
})
export class PromotionService {
    private readonly dealSubject$ = new BehaviorSubject<WelcomeDeal>(null);
    readonly welcomeDeal$: Observable<WelcomeDeal> = this.dealSubject$.asObservable();

    private request: Subscription;
    private lastToken: string;

    private readonly freeShippingSubject$ = new BehaviorSubject<FreeShippingOffer>(null);
    readonly freeShipping$: Observable<FreeShippingOffer> = this.freeShippingSubject$.asObservable();

    constructor(
        private http: HttpClient,
        private user: UserService,
        private state: TransferState,
        @Inject(PLATFORM_ID) private platformId: any
    ) {
        // The free-shipping offer is the same for everyone, so it is read everywhere - and the
        // server render's answer travels in the page: Angular 11 draws the page again when the
        // browser boots, and the home page's badge vanished until the browser's own request came
        // back, shifting everything under it, on every visit.
        const carried = this.state.get<FreeShippingOffer>(FREE_SHIPPING, null);
        if (carried) {
            this.freeShippingSubject$.next(carried);
        } else {
            this.http.get<FreeShippingOffer>(environment.apiUrl + '/api/Promotion/GetFreeShipping')
                .pipe(catchError(() => of(null)))
                .subscribe(offer => {
                    if (offer && isPlatformServer(this.platformId)) {
                        this.state.set(FREE_SHIPPING, offer);
                    }
                    this.freeShippingSubject$.next(offer);
                });
        }

        // Never during server rendering: the deal depends on who is looking.
        if (!isPlatformBrowser(this.platformId)) { return; }

        // Signing in or out changes who qualifies.
        this.user.onUserTokenChanged$.subscribe(token => {
            if (token !== this.lastToken) {
                this.lastToken = token;
                this.refresh();
            }
        });
    }

    get welcomeDeal(): WelcomeDeal {
        return this.dealSubject$.value;
    }

    get freeShipping(): FreeShippingOffer {
        return this.freeShippingSubject$.value;
    }

    /// Whether an order's items reach the offer's line, counted in whole cents like the server.
    static qualifiesForFreeShipping(offer: FreeShippingOffer, itemsTotal: number): boolean {
        return !!offer && offer.Active && offer.Over > 0
            && Math.round((itemsTotal || 0) * 100) >= Math.round(offer.Over * 100);
    }

    /// FreeShippingOffer.Charge on the server: what the buyer pays for delivery once the offer
    /// is applied - nothing up to Covers, the difference above it. In whole cents.
    static deliveryAfterOffer(offer: FreeShippingOffer, itemsTotal: number, delivery: number): number {
        if (!(delivery > 0)) { return 0; }
        if (!PromotionService.qualifiesForFreeShipping(offer, itemsTotal)) { return delivery; }
        return Math.max(0, Math.round(delivery * 100) - Math.round((offer.Covers || 0) * 100)) / 100;
    }

    /// Asks again. The cart calls this on reaching checkout and after an order, when a buyer
    /// may have stopped qualifying.
    refresh(): void {
        if (!isPlatformBrowser(this.platformId)) { return; }

        if (this.request) { this.request.unsubscribe(); }
        this.request = this.http.get<WelcomeDeal>(environment.apiUrl + '/api/Promotion/GetWelcomeDeal')
            .pipe(catchError(() => of(null)))
            .subscribe(deal => this.dealSubject$.next(deal));
    }

    /// WelcomeDeals.DiscountFor on the server, in whole cents so floating point cannot round a
    /// figure differently: 5% of $76.34 is $3.82 on both sides, and $0.505 rounds up to $0.51.
    static discountFor(deal: WelcomeDeal, subtotal: number): number {
        if (!deal || !deal.Active || !deal.Eligible || !(subtotal > 0)) { return 0; }
        if (subtotal < (deal.MinimumPurchase || 0)) { return 0; }

        const subtotalCents = Math.round(subtotal * 100);
        let cents = deal.Kind === 'percent'
            ? Math.round(subtotalCents * (deal.Value || 0) / 100)
            : Math.round((deal.Value || 0) * 100);

        if (deal.MaxDiscount > 0) {
            cents = Math.min(cents, Math.round(deal.MaxDiscount * 100));
        }

        return Math.max(0, Math.min(cents, subtotalCents)) / 100;
    }
}
